import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, ArrowLeft } from "lucide-react";
import { useEffect } from "react";

const LOGO_URL = "/atc-logo.png";

/**
 * Portal login — invite-only Google sign-in.
 * Stores intent in localStorage so the shared AuthCallback knows to route
 * the user back to /portal (not /admin) after successful Google auth.
 */
const PortalLogin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user?.is_portal_member) navigate("/portal", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    try { window.localStorage.setItem("atc-auth-intent", "portal"); } catch { /* ignore */ }
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const denied = user && !user.is_portal_member;

  return (
    <main className="min-h-screen flex items-center justify-center px-6 bg-atc-bg relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none"
           style={{
             background:
               "radial-gradient(ellipse at 30% 30%, rgba(30,144,255,0.28) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(63,191,63,0.15) 0%, transparent 55%)",
           }}
      />
      <div className="absolute top-6 left-6">
        <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-white" data-testid="portal-back-home">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
      </div>

      <div className="relative glass rounded-3xl p-10 w-full max-w-md text-center" data-testid="portal-login-card">
        <img src={LOGO_URL} alt="ATC" className="h-20 w-20 mx-auto object-contain" />
        <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-xs uppercase tracking-[0.2em] text-slate-300">
          <Lock className="h-3.5 w-3.5 text-atc-blue" /> Dealer &amp; Customer Portal
        </div>
        <h1 className="mt-6 font-display font-black text-3xl tracking-tight text-white">
          Welcome to the ATC portal
        </h1>
        <p className="text-slate-400 text-sm mt-3">
          Training videos, manuals, equipment reorders, and direct support. Access is invite-only — sign in with the Google address ATC invited.
        </p>
        {denied && (
          <div className="mt-5 px-4 py-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs" data-testid="portal-denied-banner">
            Your Google account isn&apos;t on the portal invite list yet.<br />
            Signed in as <span className="text-white">{user.email}</span>. Please contact ATC to request access.
          </div>
        )}
        <button
          onClick={handleLogin}
          className="mt-8 w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition"
          data-testid="portal-google-login"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.997 10.997 0 0 0 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.36-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          Continue with Google
        </button>
        <p className="text-xs text-slate-500 mt-6">
          Need portal access? <a href="mailto:media@atcpestcontrol.com" className="text-atc-blue hover:underline">Contact ATC</a>
        </p>
      </div>
    </main>
  );
};

export default PortalLogin;
